<template>
    <q-select
        :model-value="selection"
        :options="options"
        :display-value="displayValue"
        :option-label="optionLabel"
        :multiple="multiple"
        :disable="disable"
        stack-label
        filled
        dense
        hide-bottom-space
        :clearable="!noClear"
        @update:model-value="emitOptionSelection"
    >
        <template #option="{ itemProps, opt }">
            <q-item v-bind="itemProps">
                <q-item-section>
                    <q-item-label>{{ $t(opt[optionLabel] || opt.value || opt.label) }}</q-item-label>
                </q-item-section>
            </q-item>
        </template>

        <template v-if="useChips && multiple" #selected-item="{ opt, tabindex, index, removeAtIndex }">
            <q-chip
                removable
                dense
                :tabindex="tabindex"
                color="white"
                text-color="secondary"
                class="q-ma-none"
                @remove="removeAtIndex(index)"
            >
                {{ $t(opt[optionLabel]) }}
            </q-chip>
        </template>
    </q-select>
</template>

<script setup>
// Wrapper for a standard q-select but allows us to bind to an ID or multiple IDs
import { isNil } from 'lodash-es'
import { computed } from 'vue'

const props = defineProps({
    modelValue: null, // an ID, array of IDs (if using multiple prop) or null
    options: Array, // The options to put into the dropdown
    multiple: Boolean, // Allow multiple selection? Must bind using an array (e.g v-model="users")
    useChips: Boolean, // show selected labels rather than a total number
    noClear: Boolean, // Prevent null values / no selection state
    disable: Boolean,
    placeholder: String,

    // The property of each provided option to use as the display label
    optionLabel: {
        type: String,
        default: 'name',
    },

    // The property of each provided option to use as the value binding
    optionValue: {
        type: String,
        default: 'id',
    },

    emitObject: Boolean,
})
const emit = defineEmits(['update:model-value'])

const selection = computed(() => {
    if (isNil(props.modelValue)) return null

    if (props.emitObject) return props.modelValue

    return props.multiple
        ? props.options.filter(opt => props.modelValue.includes(opt[props.optionValue]))
        : props.options.find(opt => opt[props.optionValue] === props.modelValue)
})

const displayValue = computed(() => {
    if (!selection.value || (props.multiple && !props.modelValue.length)) {
        return props.placeholder || trans('not_selected')
    }

    return props.multiple
        ? trans(selection.value.length === 1 ? 'num_item_selected' : 'num_items_selected', { num: selection.value.length })
        : trans(selection.value[props.optionLabel] || selection.value.value || selection.value.label)
})

function emitOptionSelection(newSelection) {
    if (!newSelection) return emit('update:model-value', props.multiple ? [] : null)

    let newVal = props.multiple
        ? (props.emitObject ? newSelection : newSelection.map(obj => obj[props.optionValue]))
        : props.emitObject ? newSelection : newSelection[props.optionValue]

    emit('update:model-value', newVal)
}
</script>
